import i18n, { createInstance, i18n as I18nInstance } from 'i18next'
import { initReactI18next } from 'react-i18next'
import enTranslations from '../locales/en.json'
import frTranslations from '../locales/fr.json'

// Deep merge function to properly combine translation objects
function deepMerge(target: any, source: any): any {
  if (source === null || source === undefined) return target
  if (target === null || target === undefined) return source

  const result = { ...target }

  for (const key of Object.keys(source)) {
    const value = (source as Record<string, unknown>)[key]
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = deepMerge((target as Record<string, unknown>)[key] || {}, value)
    } else {
      (result as Record<string, unknown>)[key] = value
    }
  }

  return result
}

export const defaultResources = {
  en: {
    translation: enTranslations
  },
  fr: {
    translation: frTranslations
  }
}

// Track if we've already set up the initial configuration
let sharedInstance: I18nInstance | null = null

export const initializeI18n = (language?: string, customResources?: Record<string, { translation: any }>, { isolated = false }: { isolated?: boolean } = {}) => {
  const lang = language ?? 'en'
  const useIsolated = isolated || typeof window === 'undefined'
  const instance = useIsolated ? createInstance() : (sharedInstance ?? i18n)
  const resources = customResources ?
    deepMerge(defaultResources, customResources) :
    defaultResources

  // Only initialize once, then just update language and resources
  if (useIsolated || !instance.isInitialized) {
      instance
        .use(initReactI18next)
        .init({
          resources,
          lng: lang,
          fallbackLng: 'en',
          interpolation: {
            escapeValue: false // React already does escaping
          },
          react: {
            useSuspense: false // Disable suspense for SSR compatibility
          }
        })
    if (!useIsolated) {
      sharedInstance = instance
    }
  } else {
    // If already set up, just update language and resources
    if (instance.language !== lang) {
      instance.changeLanguage(lang)
    }

    // Update resources if provided
    if (customResources) {
      Object.keys(customResources).forEach(l => {
        const customTranslation = customResources[l]?.translation
        if (!customTranslation) return
        const existingResources = instance.hasResourceBundle(l, 'translation')
          ? instance.getResourceBundle(l, 'translation')
          : {}
        const mergedResources = deepMerge(existingResources, customTranslation)
        // Replace the entire bundle with merged resources
        instance.addResourceBundle(l, 'translation', mergedResources, true, true)

      })
    }
  }

  return instance
}

export default i18n
